import React, { useContext, useMemo, useReducer, useState } from "react";

import NavigationMenuContext from "./NavigationMenuContext";
import GeneralPageContext from "../../templates/GeneralPageContext";

import { CountrySelectionBanner } from "./CountrySelectionBanner/CountrySelectionBanner";
import { TopBar } from "./TopBar/TopBar";
import { MainNavigation } from "./MainNavigation/MainNavigation";
import { SubNavigation } from "./SubNavigation/SubNavigation";

import * as styles from "./component.module.less";
import { useIsDesktop } from "../../lib/hooks/mediaQueries";
import { SatelliteNavigation } from "./SatelliteNavigation/SatelliteNavigation";

/**
 * navMap structure
 * {
 *   navElementId: {
 *      id: String,
 *      level: Int,
 *      position: Int,
 *      link: { url: String }
 *      externalLink: String
 *      subNavElements: [String]
 *   }
 * }
 */

export function Navigation({
    navData: { level: initialLevel, treeChildren: mainNavElements },
    satelliteNavData: { treeChildren: satelliteMainNavElements }
}) {
    const {
        layout: { showMainNavigation, slimTopBar, showCountryBanner }
    } = useContext(GeneralPageContext);
    const navMap = useMemo(
        () => reduceNavElements(mainNavElements, initialLevel),
        [mainNavElements, initialLevel]
    );

    const getNavElement = (navElementId) => {
        return navMap[navElementId] || {};
    };

    const isMobile = useIsDesktop();

    function reducer(state, action) {
        const { previousLayers, activeLayer } = state;
        let newActive = {};

        switch (action.type) {
            case "navigate":
                if (!action.to) {
                    // TODO: Throw Error
                    return state;
                }
                newActive = getNavElement(action.to);

                if (newActive.level === activeLayer.level) {
                    previousLayers.pop();
                }

                if (activeLayer.id && newActive.level > 1) {
                    previousLayers.push(activeLayer.id);
                }

                return { ...state, previousLayers, activeLayer: newActive };
            case "back":
                const previousId = previousLayers.pop();
                newActive = getNavElement(previousId);
                return { ...state, previousLayers, activeLayer: newActive };
            case "closeSubNav":
                return { ...state, previousLayers: [], activeLayer: {} };
            case "toggleMobileNav":
                if (action.navOpen === undefined) {
                    // TODO: Throw Error
                    return state;
                }
                return {
                    ...state,
                    navigationOpen: action.navOpen,
                    previousLayers: [],
                    activeLayer: {}
                };
            default:
                return state;
        }
    }

    const [navState, dispatch] = useReducer(reducer, {
        previousLayers: [],
        activeLayer: {},
        navigationOpen: !isMobile
    });

    const [isBackLink, setIsBackLink] = useState(false);

    const navContext = {
        navigationReducer: { navState, dispatch },
        isMobile,
        isBackLink,
        setIsBackLink,
        getNavElement
    };

    return (
        <NavigationMenuContext.Provider value={navContext}>
            {showCountryBanner && <CountrySelectionBanner />}

            <div
                className={styles.navigationHeader}
                data-datocms-noindex={true}
            >
                <TopBar satelliteMainNavElements={satelliteMainNavElements} />

                {showMainNavigation && (
                    <MainNavigation mainNavElements={mainNavElements} />
                )}

                <SubNavigation navMap={navMap} />

                {!slimTopBar && isMobile && (
                    <SatelliteNavigation
                        satelliteMainNavElements={satelliteMainNavElements}
                        hidden={
                            !navState.navigationOpen || navState.activeLayer.id
                        }
                    />
                )}
            </div>
        </NavigationMenuContext.Provider>
    );
}

function reduceNavElements(array, initialLevel) {
    return array.reduce((navMap, navElement) => {
        const { level, treeChildren = [], ...otherProps } = navElement;
        const newElement = {};

        newElement[navElement.id] = {
            level: level - initialLevel,
            subNavElements: treeChildren
                .sort(
                    ({ position: positionA }, { position: positionB }) =>
                        positionA - positionB
                )
                .map(({ id }) => id),
            ...otherProps
        };

        const childElementMap = reduceNavElements(treeChildren, initialLevel);

        return { ...navMap, ...newElement, ...childElementMap };
    }, {});
}
